import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Lock, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import api, { API_URL } from '../services/api';
import { toast } from 'sonner';

const SetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing setup link. Please check your email for the correct link.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post(`${API_URL}/admin/trial/setup-password`, {
        token,
        password,
      });
      setSuccess(true);
      toast.success('Password set successfully!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to set password. The link may have expired.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-full max-w-md px-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/salesqualifyd-logo.png" alt="Salesqualifyd" className="h-14 mx-auto" />
          <p className="text-gray-500 mt-2 text-sm">Set up your account to get started</p>
        </div>

        <Card className="border shadow-lg" style={{ background: 'white' }}>
          <CardHeader className="text-center pb-2">
            {success ? (
              <>
                <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #059669, #10b981)' }}>
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl" data-testid="password-set-success-title">You're All Set!</CardTitle>
                <CardDescription>Your password has been created. You can now log in.</CardDescription>
              </>
            ) : (
              <>
                <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #8B1A1A, #330066)' }}>
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl" data-testid="set-password-title">Set Your Password</CardTitle>
                <CardDescription>Create a secure password for your account</CardDescription>
              </>
            )}
          </CardHeader>

          <CardContent>
            {success ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 text-center">
                  Your 72-hour full access trial begins when you log in.
                </p>
                <Button
                  data-testid="go-to-login-btn"
                  className="w-full font-semibold text-white"
                  style={{ background: '#8B1A1A' }}
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="set-password-error">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="set-password-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={!token || loading}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    data-testid="set-password-confirm-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Repeat your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={!token || loading}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="set-password-submit-btn"
                  className="w-full font-semibold text-white"
                  style={{ background: '#8B1A1A' }}
                  disabled={!token || loading}
                >
                  {loading ? 'Setting up...' : 'Set Password & Get Started'}
                </Button>

                <p className="text-xs text-gray-500 text-center mt-3">
                  By setting your password, you agree to our{' '}
                  <a href="/terms-of-use" className="underline" style={{ color: '#330066' }}>Terms of Use</a> and{' '}
                  <a href="/privacy-policy" className="underline" style={{ color: '#330066' }}>Privacy Policy</a>.
                </p>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-gray-500 text-xs mt-6">
          Questions? Contact <a href="mailto:support@salesqualifyd.com" className="underline" style={{ color: '#8B1A1A' }}>support@salesqualifyd.com</a>
        </p>
      </div>
    </div>
  );
};

export default SetPasswordPage;
